import { useEffect, useRef, useState } from 'react';
import { reachApi } from '../api.js';
import { ButtonSpinner, LoadingState } from './ui.jsx';

export const destinationKey = draft => [draft.connectionId, draft.datasetId, draft.tableId].join('|');

// Google posts an authorization code only. No Google access/refresh token reaches the SPA.
export function waitForGoogle(popup, pending, signal) {
  return new Promise((resolve, reject) => {
    let timer;
    const cleanup = () => {
      window.removeEventListener('message', receive);
      signal.removeEventListener('abort', abort);
      clearInterval(timer);
    };
    const fail = message => { cleanup(); popup.close(); reject(new Error(message)); };
    const abort = () => fail('Google sign-in cancelled.');
    const receive = event => {
      if (event.origin !== pending.callbackOrigin || event.source !== popup
          || event.data?.type !== 'reach-gcp-oauth' || event.data.state !== pending.state) return;
      cleanup();
      popup.close();
      if (event.data.error) reject(new Error('Google sign-in was not approved. Start sign-in again.'));
      else if (!event.data.code) reject(new Error('Google did not return a sign-in code. Try again.'));
      else resolve({ code: event.data.code, state: pending.state });
    };
    window.addEventListener('message', receive);
    signal.addEventListener('abort', abort, { once: true });
    const started = Date.now();
    // Give postMessage a moment to arrive after the callback closes its popup.
    let closedAt = 0;
    timer = setInterval(() => {
      if (popup.closed) closedAt ||= Date.now();
      if (closedAt && Date.now() - closedAt > 1000) fail('Google sign-in was closed. Try again.');
      else if (Date.now() - started > 600000) fail('Google sign-in timed out. Start again.');
    }, 500);
    if (signal.aborted) abort();
  });
}

export function GcpConnectionDialog({ onClose, onSave, existing = null }) {
  const [form, setForm] = useState({ name: existing?.name || '', projectId: existing?.projectId || '',
    authType: existing?.authType || 'service-account', serviceAccount: '' });
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const controller = useRef(null);
  const popupRef = useRef(null);
  const mounted = useRef(true);
  const dialogRef = useRef(null);
  useEffect(() => {
    mounted.current = true;
    const previous = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.querySelector('input')?.focus();
    reachApi.gcpConfiguration().then(value => { if (mounted.current) setConfig(value); })
      .catch(exc => { if (mounted.current) setConfigError(exc.message); });
    return () => {
      mounted.current = false;
      controller.current?.abort();
      popupRef.current?.close();
      document.body.style.overflow = previousOverflow;
      previous?.focus();
    };
  }, []);
  const update = (key, value) => { setForm(current => ({ ...current, [key]: value })); setError(''); };
  const valid = form.name.trim() && form.projectId.trim()
    && (form.authType === 'service-account' ? form.serviceAccount.trim() : config?.googleSignInReady);
  const connect = async event => {
    event.preventDefault();
    if (!valid || busy) return;
    // Open synchronously in the click handler; otherwise browsers block the OAuth popup.
    const popup = form.authType === 'oauth' ? window.open('about:blank', 'reach-google-connect', 'popup,width=600,height=720') : null;
    if (form.authType === 'oauth' && !popup) { setError('Allow pop-ups for Reach, then start Google sign-in again.'); return; }
    popupRef.current = popup;
    setBusy(true);
    setError('');
    try {
      const details = { name: form.name.trim(), projectId: form.projectId.trim(), connectionId: existing?.id || '' };
      let saved;
      if (form.authType === 'service-account') {
        saved = await reachApi.connectGcpServiceAccount({ ...details, serviceAccount: form.serviceAccount });
      } else {
        controller.current = new AbortController();
        const pending = await reachApi.startGcpSignIn(details);
        if (!mounted.current || controller.current.signal.aborted) { popup.close(); return; }
        if (popup.closed) throw new Error('Google sign-in was closed. Try again.');
        const completion = waitForGoogle(popup, pending, controller.current.signal);
        try { popup.location.href = pending.authorizationUrl; }
        catch {
          controller.current.abort();
          await completion;
        }
        saved = await reachApi.finishGcpSignIn(await completion);
      }
      if (mounted.current) { setForm(current => ({ ...current, serviceAccount: '' })); onSave(saved); }
    } catch (exc) {
      popup?.close();
      if (mounted.current) setError(exc.message || 'The connection could not be verified. Try again.');
    } finally { if (mounted.current) setBusy(false); }
  };
  const upload = async event => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 32768) { setError('Use a service-account JSON key smaller than 32 KB.'); return; }
    try {
      const text = await file.text();
      const key = JSON.parse(text);
      if (key.type !== 'service_account' || !key.client_email || !key.private_key) throw new Error();
      if (mounted.current) { update('serviceAccount', text); if (!form.projectId) update('projectId', key.project_id || ''); }
    } catch { if (mounted.current) setError('This file is not a valid Google service-account JSON key.'); }
  };
  const onKeyDown = event => {
    if (event.key === 'Escape') { event.preventDefault(); onClose(); }
    if (event.key === 'Tab') {
      const nodes = [...dialogRef.current.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled)')];
      const first = nodes[0], last = nodes.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  };
  return <div className="o-scrim" role="presentation" onClick={onClose}>
    <form ref={dialogRef} className="o-modal reach-gcp-modal" role="dialog" aria-modal="true"
      aria-labelledby="gcp-connection-title" aria-busy={busy} onClick={event => event.stopPropagation()}
      onKeyDown={onKeyDown} onSubmit={connect}>
      <div className="o-modal-head"><div><h2 id="gcp-connection-title">{existing ? 'Reconnect BigQuery' : 'Connect Google BigQuery'}</h2>
        <p className="o-note">Choose how Reach authenticates to your destination.</p></div>
        <button type="button" className="o-btn o-btn-ghost o-btn-sm" aria-label="Close connection dialog" onClick={onClose}>×</button></div>
      <div className="o-modal-body reach-gcp-body">
        <label><span className="o-field-lbl">Authentication method</span><select className="o-control" aria-label="Authentication method" disabled={busy} value={form.authType} onChange={event => update('authType', event.target.value)}><option value="service-account">Service account</option><option value="oauth">My Google account</option></select></label>
        <div className="reach-gcp-grid">
          <label><span className="o-field-lbl">Connection name</span><input className="o-control" maxLength={100} disabled={busy} value={form.name} onChange={event => update('name', event.target.value)} placeholder="Client reporting warehouse" /></label>
          <label><span className="o-field-lbl">Google Cloud project ID</span><input className="o-control" disabled={busy} value={form.projectId} onChange={event => update('projectId', event.target.value)} placeholder="marketing-reporting" autoComplete="off" spellCheck={false} /></label>
        </div>
        {form.authType === 'service-account' ? <>
          <div className="o-hint"><b>Recommended for unattended exports.</b> Use a dedicated service account with access to the destination dataset. It stays connected while its key and permissions remain valid.</div>
          <label><span className="o-field-lbl">Service-account JSON key</span><input type="file" className="o-control" accept=".json,application/json" disabled={busy} onChange={upload} /></label>
          <label><span className="o-field-lbl">Or paste the JSON key</span><textarea className="o-control reach-gcp-key" rows={4} disabled={busy} value={form.serviceAccount} onChange={event => update('serviceAccount', event.target.value)} autoComplete="off" spellCheck={false} placeholder="Paste the service-account JSON here" /></label>
        </> : <>
          <div className="o-hint"><b>Automatic renewal.</b> Sign in once and approve BigQuery access. Reach renews access on the server using an encrypted refresh token. Google or your administrator can still require you to reconnect.</div>
          {!config && !configError && <LoadingState title="Checking Google sign-in configuration…" rows={0} />}
          {configError && <p className="o-state o-error" role="alert">{configError}</p>}
          {config && !config.googleSignInReady && <div className="o-hint"><b>Google sign-in needs setup.</b> {config.reason}</div>}
        </>}
        <p className="o-note">Keys and refresh tokens are encrypted server-side—not saved in code files or browser storage. Connection creation requires Reach administrator access.</p>
        <p className="o-note">This verifies authentication and dataset visibility, not write access. Grant BigQuery Data Editor on the destination dataset; job-based delivery also needs BigQuery Job User on the project.</p>
        {error && <div className="o-state o-error" role="alert">{error}</div>}
      </div>
      <div className="o-modal-foot"><button type="button" className="o-btn" onClick={onClose}>{busy ? 'Close' : 'Cancel'}</button>
        <button type="submit" className="o-btn o-btn-primary" disabled={!valid || busy}>{busy && <ButtonSpinner />}{busy ? 'Connecting…' : form.authType === 'oauth' ? 'Sign in with Google' : 'Verify and save connection'}</button></div>
    </form>
  </div>;
}

export function GcpDestinationPicker({ connections, setConnections, draft, setDraft, onColumns, onConnect }) {
  const [datasets, setDatasets] = useState({ key: '', items: [] });
  const [tables, setTables] = useState({ key: '', items: [] });
  const [state, setState] = useState({ loading: true, error: '', message: '' });
  const [retry, setRetry] = useState(0);
  const [listReady, setListReady] = useState(false);
  const [columnPreview, setColumnPreview] = useState([]);
  const connection = connections.find(item => item.id === draft.connectionId);
  useEffect(() => {
    let current = true;
    setState({ loading: true, error: '', message: 'Loading BigQuery connections…' });
    onColumns({ key: '', columns: [] });
    setListReady(false);
    reachApi.listGcpConnections().then(rows => { if (current) { setConnections(rows); setListReady(true); } })
      .catch(exc => { if (current) setState({ loading: false, error: exc.message, message: '' }); });
    return () => { current = false; };
  }, [retry, setConnections, onColumns]);
  useEffect(() => {
    if (!listReady) return undefined;
    let current = true;
    const key = destinationKey(draft);
    onColumns({ key: '', columns: [] });
    setColumnPreview([]);
    if (!connection) { setState({ loading: false, error: '', message: '' }); return undefined; }
    if (connection.status !== 'active') { setState({ loading: false, error: 'This connection needs reconnecting before it can be used.', message: '' }); return undefined; }
    setState({ loading: true, error: '', message: 'Loading destinations from Google BigQuery…' });
    (async () => {
      try {
        const selection = { connectionId: connection.id };
        const data = await reachApi.gcpMetadata(selection);
        if (!current) return;
        setDatasets({ key: connection.id, items: data.datasets });
        if (draft.datasetId) {
          if (!data.datasets.some(item => item.id === draft.datasetId)) throw new Error('This dataset is no longer accessible. Select another dataset.');
          const tableData = await reachApi.gcpMetadata({ ...selection, datasetId: draft.datasetId });
          if (!current) return;
          setTables({ key: [connection.id, draft.datasetId].join('|'), items: tableData.tables.filter(item => item.type === 'TABLE') });
          if (draft.tableId) {
            const schema = await reachApi.gcpMetadata({ ...selection, datasetId: draft.datasetId, tableId: draft.tableId });
            if (!current) return;
            onColumns({ key, columns: schema.columns });
            setColumnPreview(schema.columns);
          }
        }
        if (current) setState({ loading: false, error: '', message: 'Google authentication and metadata access verified. Write permissions are not tested.' });
      } catch (exc) { if (current) setState({ loading: false, error: exc.message, message: '' }); }
    })();
    return () => { current = false; };
  }, [listReady, connection?.id, connection?.status, connection?.updatedAt, draft.datasetId, draft.tableId, retry, onColumns]);
  const ds = datasets.key === connection?.id ? datasets.items : [];
  const ts = tables.key === [connection?.id, draft.datasetId].join('|') ? tables.items : [];
  return <section className="o-panel reach-gcp-destination">
    <div className="o-head-row"><div><h3>Google BigQuery destination</h3><p className="o-note">Connect, choose a dataset and table, then map to its actual schema.</p></div>
      <div className="o-ctrl-row"><button type="button" className="o-btn o-btn-sm" disabled={state.loading} onClick={() => setRetry(current => current + 1)}>Refresh / test access</button>
        {connection && <button type="button" className="o-btn o-btn-sm" onClick={() => onConnect(connection)}>Reconnect</button>}
        <button type="button" className="o-btn o-btn-sm" onClick={() => onConnect(null)}>+ Add connection</button></div></div>
    <div className="reach-gcp-grid reach-gcp-grid-three">
      <label><span className="o-field-lbl">Connection / project</span><select className="o-control" value={draft.connectionId} disabled={!listReady} onChange={event => setDraft(current => ({ ...current, connectionId: event.target.value, datasetId: '', tableId: '', mappings: {} }))}><option value="">Select a connection</option>{connections.map(item => <option key={item.id} value={item.id}>{item.name} · {item.projectId}{item.status !== 'active' ? ' · Reconnect required' : ''}</option>)}</select></label>
      <label><span className="o-field-lbl">Dataset</span><select className="o-control" value={draft.datasetId} disabled={state.loading || !ds.length} onChange={event => setDraft(current => ({ ...current, datasetId: event.target.value, tableId: '', mappings: {} }))}><option value="">Select a dataset</option>{ds.map(item => <option key={item.id}>{item.id}</option>)}</select></label>
      <label><span className="o-field-lbl">Table</span><select className="o-control" value={draft.tableId} disabled={state.loading || !ts.length} onChange={event => setDraft(current => ({ ...current, tableId: event.target.value, mappings: {} }))}><option value="">Select a table</option>{ts.map(item => <option key={item.id}>{item.id}</option>)}</select></label>
    </div>
    {connection && <p className="o-note reach-gcp-identity">{connection.authType === 'oauth' ? 'Google account' : 'Service account'} · {connection.identity}</p>}
    {columnPreview.length > 0 && <details className="reach-gcp-schema" open>
      <summary>Table columns ({columnPreview.length})</summary>
      <div className="reach-gcp-schema-scroll"><table className="o-tbl"><thead><tr><th>Column</th><th>Type</th><th>Mode</th></tr></thead>
        <tbody>{columnPreview.map(column => <tr key={column.name}><td className="o-mono">{column.name}</td><td className="o-mono">{column.type}</td><td>{column.mode || 'NULLABLE'}{column.hasDefault ? ' · Default value' : ''}</td></tr>)}</tbody>
      </table></div>
    </details>}
    {state.loading && <LoadingState title={state.message} rows={0} />}
    {state.error && <div className="o-state o-error" role="alert">{state.error}<button type="button" className="o-btn o-btn-sm" onClick={() => setRetry(current => current + 1)}>Retry</button></div>}
    {!state.loading && state.message && <p className="o-note" role="status">{state.message}</p>}
    {listReady && !connections.length && <p className="o-hint"><b>No BigQuery connection in this workspace.</b> Add a verified connection. Old local-only placeholders need reconnecting.</p>}
    {!state.loading && !state.error && connection && !ds.length && <p className="o-hint"><b>No datasets visible.</b> Create a dataset in Google Cloud or grant this account dataset access.</p>}
    {!state.loading && !state.error && draft.datasetId && ds.length > 0 && !ts.length && <p className="o-hint"><b>No writable tables visible.</b> Create your destination table in Google Cloud, then refresh this list. Views and external tables are not export destinations.</p>}
  </section>;
}
